import { CommercialTermsPriceEstimator,
  FixedOneTimePriceEstimator,
  SubscriptionPriceEstimator,
  getBillingIntervalInMonths,
  isCommercialTermsProduct,
  isCommissionProduct,
  isFixedProduct,
  isOneTimeProduct,
  isSubscriptionProduct,
  isUsageProduct, isInstallmentsProduct, InstallmentsPriceEstimator, isFixedPerUnitProduct, FixedPerUnitPriceEstimator, 
  isRefundProduct, 
  RefundPriceEstimator } from '@weft/services';
import type { BillingInterval, FixedProductInput, ProductInput, UsageProductInput } from '@weft/types';
import { isNotNullOrUndefined, raise } from '@weft/utils';

export function getProductInputEstimation(product: ProductInput, billingPeriod: BillingInterval) {
  if (isFixedProduct<FixedProductInput>(product)) {
    return getFixedProductEstimation(product, billingPeriod);
  }

  if (isUsageProduct<UsageProductInput>(product)) {
    return getUsageProductInputEstimation(product, billingPeriod);
  }

  return raise(`Unknown product type: ${JSON.stringify(product, null, 2)}`);
}

export function getFixedProductEstimation(product: FixedProductInput, billingPeriod: BillingInterval) {
  if (isOneTimeProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;

    return isNotNullOrUndefined(price) && !isNaN(price)
      ? new FixedOneTimePriceEstimator().estimate(product, undefined)
      : 0;
  }

  if (isRefundProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;

    return isNotNullOrUndefined(price) && !isNaN(price)
      ? new RefundPriceEstimator().estimate(product, undefined)
      : 0;
  }
  if (isSubscriptionProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;

    return isNotNullOrUndefined(price) && !isNaN(price)
      ? new SubscriptionPriceEstimator().estimate(product, billingPeriod)
      : 0;
  }

  if (isCommissionProduct(product)) {
    return 0;
  }

  if (isCommercialTermsProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;

    return isNotNullOrUndefined(price) && !isNaN(price)
      ? new CommercialTermsPriceEstimator().estimate(product, undefined)
      : 0;
  }

  if (isInstallmentsProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;
    const installments = product.variants[0].pricing.priceConfiguration?.installments;
    const isPriceValid = isNotNullOrUndefined(price) && !isNaN(price);
    const isInstallmentsValid = isNotNullOrUndefined(installments) && !isNaN(installments);

    return isPriceValid && isInstallmentsValid
      ? new InstallmentsPriceEstimator().estimate(product, undefined)
      : 0;
  }

  if (isFixedPerUnitProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;
    const units = product.variants[0].pricing.priceConfiguration?.units;
    const isPriceValid = isNotNullOrUndefined(price) && !isNaN(price);
    const isUnitsValid = isNotNullOrUndefined(units) && !isNaN(units);

    return isPriceValid && isUnitsValid
      ? new FixedPerUnitPriceEstimator().estimate(product, undefined)
      : 0;
  }
  
  return raise(`Unknown fixed pricing model: ${product.variants[0].pricing.pricingModel}`);
}

export function getUsageProductInputEstimation(product: UsageProductInput, billingPeriod: BillingInterval) {
  const billingPeriodInMonths = getBillingIntervalInMonths(billingPeriod);

  return (product.basePrice ?? 0) * (12 / billingPeriodInMonths);
}